import React, { forwardRef } from "react";

import { useTranslation } from "utils";

import {
  COVER_CROP_FIT_STATUS,
  COVER_CROP_TYPE_OPTIONS,
  type CoverCropMixType,
  IRRIGATION_OPTIONS,
  RESIDUE_OPTIONS,
  SCENARIO_LIMITS,
  type SoilSightTillage,
  TILLAGE_OPTIONS,
} from "consts";

import { Button, Icon, Option, OverflowEllipsisWithTooltip, Select, Tooltip } from "components";

import { CoverCropFitWarning } from "pages/app/scenario-analysis/components/crop-rotation/CoverCropFitWarning";
import type { RecommendedPracticeField, ScenarioMainCropOption } from "pages/app/scenario-analysis/types/scenario";
import type { CoverCropFit } from "pages/app/scenario-analysis/utils/coverCropFit";
import { cropYearHasAnyEntry, getResidueLockState } from "pages/app/scenario-analysis/utils/cropYearHelpers";

export interface YearRowProps {
  yearNumber: number;
  totalYears: number;
  crop?: string;
  /** When true, shows an info hint below the crop cell explaining the detected crop is unsupported. */
  hasUnresolvableCrop?: boolean;
  coverCropId?: CoverCropMixType | null;
  tillageId?: SoilSightTillage | null;
  residue?: boolean | null;
  irrigation?: boolean | null;
  mainCropOptions: ScenarioMainCropOption[];
  isMainCropOptionsLoading?: boolean;
  /** `lockedResidue` is set when the newly selected crop forces the residue field per `getResidueLockState` — must be applied together with `value` in a single update. */
  onCropChange?: (value: string, lockedResidue?: boolean) => void;
  onCoverCropIdChange?: (value: string) => void;
  onTillageChange?: (value: string) => void;
  onResidueChange?: (value: boolean) => void;
  onIrrigationChange?: (value: boolean) => void;
  onRefine?: () => void;
  onRemove?: () => void;
  /** Called when the row can't be removed (minimum rows reached) — clears all inputs instead. */
  onClear?: () => void;
  dataTestId?: string;
  className?: string;
  cropError?: boolean;
  residueError?: boolean;
  tillageError?: boolean;
  /** Onboarding: practice fields of this row to visually mark as auto-recommended. */
  recommendedFields?: RecommendedPracticeField[];
  /** Onboarding: hides the refine action entirely. */
  hideRefineAction?: boolean;
  /** Onboarding step 3: hides row deletion (rows are fixed by the baseline). */
  hideDeleteAction?: boolean;
  /** Onboarding step 3: locks the crop select (practices remain editable). */
  disableCropSelection?: boolean;
  /** The scenario builder splits delete and the economics link into their own columns; onboarding keeps one actions cell. */
  variant?: "builder" | "onboarding";
  dragHandleProps?: React.HTMLAttributes<HTMLElement> | null;
  sortableStyle?: React.CSSProperties;
  isDragging?: boolean;
  /** A `wont_fit` result outlines the cover crop select in amber and shows an inline warning under the row. */
  coverCropFit?: CoverCropFit;
}

export const YearRow = forwardRef<HTMLDivElement, YearRowProps>(
  (
    {
      yearNumber,
      totalYears,
      crop,
      coverCropId,
      tillageId,
      residue,
      irrigation,
      onCropChange,
      onCoverCropIdChange,
      onTillageChange,
      onResidueChange,
      onIrrigationChange,
      onRefine,
      onRemove,
      onClear,
      cropError = false,
      hasUnresolvableCrop = false,
      residueError = false,
      tillageError = false,
      className = "",
      dataTestId = "",
      mainCropOptions,
      isMainCropOptionsLoading = false,
      dragHandleProps,
      sortableStyle,
      isDragging = false,
      recommendedFields,
      hideRefineAction = false,
      hideDeleteAction = false,
      variant = "builder",
      disableCropSelection = false,
      coverCropFit,
    },
    ref
  ) => {
    const { t } = useTranslation("scenarioAnalysis");
    const isBuilder = variant === "builder";
    const hasMainCrop = Boolean(crop?.trim());
    const isResidueDisabledForCrop = getResidueLockState(mainCropOptions, crop ?? "").disabled;
    const hasAnyEntry = cropYearHasAnyEntry({ crop: crop ?? "", coverCropId, tillageId, residue });
    const canDelete = totalYears > SCENARIO_LIMITS.MIN_YEARS || hasAnyEntry;
    const coverCropWontFit = coverCropFit?.status === COVER_CROP_FIT_STATUS.WONT_FIT ? coverCropFit : undefined;

    const getCropLabel = (cropId?: string) => mainCropOptions.find((option) => option.value === cropId)?.label ?? "";

    const renderSelect = ({
      value,
      onChange,
      options,
      placeholder,
      dataTestId: selectDataTestId,
      disabled = false,
      hasError = false,
      hasWarning = false,
      isLoading = false,
      popupMatchSelectWidth,
      showSearch = false,
    }: {
      value?: string | null;
      onChange?: (value: string) => void;
      options: { value: string; label: string }[];
      placeholder?: string;
      dataTestId: string;
      disabled?: boolean;
      hasError?: boolean;
      hasWarning?: boolean;
      isLoading?: boolean;
      popupMatchSelectWidth?: boolean | number;
      showSearch?: boolean;
    }) => (
      <Select
        value={value ?? ""}
        onChange={(nextValue: string) => onChange?.(nextValue ?? "")}
        dataTestId={selectDataTestId}
        disabled={disabled}
        isLoading={isLoading}
        defaultActiveFirstOption={false}
        className={[
          "scenario-analysis-table-select",
          hasError && "scenario-analysis-table-select-error",
          hasWarning && "scenario-analysis-table-select-warning",
        ]
          .filter(Boolean)
          .join(" ")}
        label=""
        optionFilterProp="label"
        showSearch={showSearch}
        allowClear={false}
        popupMatchSelectWidth={popupMatchSelectWidth}
        labelRender={({ value: v, label }) => {
          if ((!v || isLoading) && placeholder) {
            return (
              <span className="scenario-analysis-table-select-placeholder" data-testid={`${selectDataTestId}-placeholder`}>
                {placeholder}
              </span>
            );
          }
          if (typeof label !== "string") return label;
          return (
            <OverflowEllipsisWithTooltip title={label} className="scenario-analysis-truncating-label">
              {label}
            </OverflowEllipsisWithTooltip>
          );
        }}
      >
        {options.map((option) => (
          <Option key={option.value} value={option.value} label={option.label}>
            {option.label}
          </Option>
        ))}
      </Select>
    );

    return (
      <div
        ref={ref}
        className={`scenario-analysis-year-row ${isDragging ? "is-dragging" : ""} ${className}`.trim()}
        style={sortableStyle}
        data-testid={dataTestId}
      >
        {/* Drag Handle + Year Number — a locked crop order (onboarding change scenario) hides the handle */}
        <div className="year-cell year-number-cell">
          {!disableCropSelection && (
            <div className="drag-handle" {...(dragHandleProps ?? {})} aria-label={t("scenarioBuilder.dragToReorder")}>
              <Icon iconName="tabler/grip-vertical" size="s" />
            </div>
          )}
          <span className="year-label">
            {isBuilder ? `${t("scenarioBuilder.yearColumn")} ${yearNumber}` : `${t("scenarioBuilder.yearAbbreviation")}${yearNumber}`}
          </span>
        </div>

        {/* Crop Selector */}
        <div className={`year-cell crop-cell ${cropError ? "error" : ""}`}>
          {renderSelect({
            value: crop,
            onChange: (value) => {
              onCropChange?.(value, getResidueLockState(mainCropOptions, value).lockedValue);
            },
            options: mainCropOptions,
            placeholder: t("scenarioBuilder.selectCrop"),
            dataTestId: `${dataTestId}-main-crop`,
            disabled: disableCropSelection,
            hasError: cropError,
            isLoading: isMainCropOptionsLoading,
            showSearch: true,
          })}
          {!hasMainCrop && hasUnresolvableCrop && <span className="year-cell-crop-unresolvable-hint">{t("scenarioBuilder.unresolvableCropHint")}</span>}
        </div>

        {/* Residue Selector */}
        <div className={`year-cell residue-cell ${residueError ? "error" : ""} ${recommendedFields?.includes("residue") ? "recommended" : ""}`}>
          {renderSelect({
            value: residue === null || residue === undefined ? null : String(residue),
            onChange: (v) => onResidueChange?.(v === "true"),
            options: RESIDUE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
            placeholder: "—",
            dataTestId: `${dataTestId}-residue`,
            disabled: !hasMainCrop || isResidueDisabledForCrop,
            hasError: residueError,
            popupMatchSelectWidth: false,
          })}
        </div>

        {/* Tillage Selector */}
        <div className={`year-cell tillage-cell ${tillageError ? "error" : ""} ${recommendedFields?.includes("tillageId") ? "recommended" : ""}`}>
          {renderSelect({
            value: tillageId,
            onChange: (v) => onTillageChange?.(v),
            options: TILLAGE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
            placeholder: "—",
            dataTestId: `${dataTestId}-tillage`,
            disabled: !hasMainCrop,
            hasError: tillageError,
            popupMatchSelectWidth: false,
          })}
        </div>

        {/* Cover Crop Selector */}
        <div className={`year-cell cover-crop-cell ${recommendedFields?.includes("coverCropId") ? "recommended" : ""}`.trim()}>
          {renderSelect({
            value: coverCropId,
            onChange: (v) => onCoverCropIdChange?.(v),
            options: COVER_CROP_TYPE_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
            placeholder: !hasMainCrop ? "—" : undefined,
            dataTestId: `${dataTestId}-cover`,
            disabled: !hasMainCrop,
            hasWarning: Boolean(coverCropWontFit),
            popupMatchSelectWidth: false,
          })}
        </div>

        {/* Irrigation Selector */}
        <div className="year-cell irrigation-cell">
          {renderSelect({
            value: String(irrigation === true),
            onChange: (v) => onIrrigationChange?.(v === "true"),
            options: IRRIGATION_OPTIONS.map((o) => ({ value: o.value, label: t(o.labelKey) })),
            dataTestId: `${dataTestId}-irrigation`,
            disabled: !hasMainCrop,
            popupMatchSelectWidth: false,
          })}
        </div>

        {/* Actions — the builder splits these into a delete column and a tinted economics column. */}
        {isBuilder ? (
          <>
            <div className="year-cell delete-cell">
              {!hideDeleteAction && (
                <Button
                  className="action-btn delete-btn"
                  onClick={canDelete ? onRemove : onClear}
                  title={t("scenarioBuilder.deleteYear")}
                  dataTestId={`${dataTestId}-delete`}
                >
                  <Icon iconName="tabler/trash" size="s" />
                </Button>
              )}
            </div>
            <div className="year-cell economics-cell">
              {!hideRefineAction && (
                <Button className="action-btn refine-btn" onClick={onRefine} disabled={!hasMainCrop} dataTestId={`${dataTestId}-refine`}>
                  <Icon iconName="tabler/edit-pen" size="s" />
                  <Tooltip title={t("scenarioBuilder.editInputs")} placement="top" className="scenario-analysis-truncating-label">
                    {t("scenarioBuilder.editInputs")}
                  </Tooltip>
                </Button>
              )}
            </div>
          </>
        ) : (
          <div className="year-cell actions-cell">
            {!hideRefineAction && (
              <Button className="action-btn refine-btn" onClick={onRefine} disabled={!hasMainCrop} dataTestId={`${dataTestId}-refine`}>
                <Icon iconName="tabler/edit-pen" size="s" />
                <span>{t("scenarioBuilder.refine")}</span>
              </Button>
            )}
            {!hideDeleteAction && (
              <Button
                className="action-btn delete-btn"
                onClick={canDelete ? onRemove : onClear}
                title={t("scenarioBuilder.deleteYear")}
                dataTestId={`${dataTestId}-delete`}
              >
                <Icon iconName="tabler/trash" size="s" />
              </Button>
            )}
          </div>
        )}

        {/* Always rendered: a live region announces content added to it, not one mounted already filled. */}
        <div className="year-row-cover-crop-fit-status" role="status" data-testid={`${dataTestId}-cover-crop-fit-status`}>
          {coverCropWontFit && (
            <CoverCropFitWarning
              fit={coverCropWontFit}
              previousCropLabel={getCropLabel(coverCropWontFit.previousCropId)}
              dataTestId={`${dataTestId}-cover-crop-fit-warning`}
            />
          )}
        </div>
      </div>
    );
  }
);

YearRow.displayName = "YearRow";

export default YearRow;
